import { useEffect, useRef, useState } from 'react'

export const CANCEL_REASONS: { value: string; label: string }[] = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'not_using', label: 'Not using it enough' },
  { value: 'technical_issues', label: 'Technical problems' },
  { value: 'switching_tool', label: 'Switching to another tool' },
  { value: 'taking_a_break', label: "Taking a break, I'll be back" },
  { value: 'other', label: 'Other' },
]

const DETAILS_MAX = 2000

interface CancelSubscriptionModalProps {
  planName: string
  // Formatted end date, or null when it is not known.
  endsOn: string | null
  submitting: boolean
  error: string | null
  onKeep: () => void
  onConfirm: (reason: string, details: string) => void
}

export default function CancelSubscriptionModal({
  planName,
  endsOn,
  submitting,
  error,
  onKeep,
  onConfirm,
}: CancelSubscriptionModalProps) {
  const [reason, setReason] = useState('')
  const [details, setDetails] = useState('')
  const keepRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  // "Keep my plan" is the default focus.
  useEffect(() => {
    keepRef.current?.focus()
  }, [])

  // Escape equals "Keep my plan". Tab stays inside the dialog.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (!submitting) onKeep()
        return
      }
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input, textarea'),
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeep, submitting])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={() => { if (!submitting) onKeep() }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-dialog-title"
        className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-gray-200"
      >
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 id="cancel-dialog-title" className="text-lg font-semibold text-slate-900">
            Cancel your {planName} plan?
          </h2>
          <p className="text-sm text-gray-700 mt-2">
            {endsOn
              ? <>You&apos;ll keep {planName} until {endsOn}. After that, your account moves to the Free plan and you won&apos;t be charged again.</>
              : <>You&apos;ll keep {planName} until the end of your current billing period. After that, your account moves to the Free plan and you won&apos;t be charged again.</>}
          </p>
        </div>

        <div className="px-6 py-5 space-y-5">
          <fieldset>
            <legend className="text-sm font-semibold text-slate-900 mb-2">
              Why are you leaving? <span className="text-red-600" aria-hidden="true">*</span>
            </legend>
            <div className="space-y-1.5">
              {CANCEL_REASONS.map(r => (
                <label key={r.value} className="flex items-center gap-2.5 text-sm text-gray-800 cursor-pointer rounded-lg px-2 py-1.5 hover:bg-gray-50">
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                    disabled={submitting}
                    className="accent-gold-500"
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label htmlFor="cancel-details" className="block text-sm font-semibold text-slate-900 mb-1.5">
              Anything we could do better? <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <textarea
              id="cancel-details"
              value={details}
              onChange={e => setDetails(e.target.value.slice(0, DETAILS_MAX))}
              maxLength={DETAILS_MAX}
              rows={4}
              disabled={submitting}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-gold-500"
            />
            <p className="text-xs text-gray-500 text-right mt-1">{details.length} / {DETAILS_MAX}</p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={() => onConfirm(reason, details)}
            disabled={!reason || submitting}
            className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Cancelling…' : 'Cancel subscription'}
          </button>
          <button
            ref={keepRef}
            type="button"
            onClick={onKeep}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-gold-500 hover:bg-gold-400 text-slate-900 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500 disabled:opacity-60"
          >
            Keep my plan
          </button>
        </div>
      </div>
    </div>
  )
}
